import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { FolderOpen, Users } from "lucide-react";
import { ClubApi, type ClubCustomOrderSummary } from "../../services/clubApi";
import ClubWelcomePage from "./ClubWelcomePage";
import RankBadge from "../../components/club/RankBadge";
import { buildCasePath } from "../../utils/clubCasePath";
import { isHttpAssetUrl, normalizeEvidenceImageRecords } from "../../utils/clubAssetRefs";
import { devLog } from "../../utils/devLog";

const RANK_LABELS: Record<string, string> = {
  junior_detective: "Junior Detective",
  detective: "Detective",
  senior_detective: "Senior Detective",
  lead_detective: "Lead Detective",
  special_investigator: "Special Investigator",
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MOBILE_ARCHIVE_EPISODES_PER_PAGE = 4;
const DESKTOP_ARCHIVE_EPISODES_PER_PAGE = 6;
const isNewFormatCase = (clubCase: any) => {
  if (clubCase?.isNewFormatCase === true) return true;

  const caseIndex = Number(clubCase?.caseIndex || 0);
  if (!Number.isFinite(caseIndex) || caseIndex < 1000) return false;

  const approvalStatus = String(clubCase?.approvalStatus || "").trim().toLowerCase();
  const hasStructuredPayload =
    Array.isArray(clubCase?.casePayload?.evidence_files) && clubCase.casePayload.evidence_files.length > 0;
  const hasGeneratedCaseId = String(clubCase?.generatedCaseId || "").trim().length > 0;

  if (["approved", "published_ready", "published"].includes(approvalStatus)) return true;
  if (hasStructuredPayload) return true;
  if (hasGeneratedCaseId) return true;
  return false;
};

const TRIAL_CASE_INDEX = 1000;
const REGISTERED_DEMO_STEPS = [
  {
    key: "welcome",
    eyebrow: "Step 1",
    title: "Agency intake",
    summary:
      "Get your bearings inside the dashboard, understand how the investigation cadence works, and see where your first active-member assignment will arrive.",
    checkpoint: "Review your intake record and membership path.",
    primaryActionLabel: "Watch Agency Intake",
    primaryActionSource: "registered_demo_welcome_video",
  },
  {
    key: "free_case",
    eyebrow: "Step 2",
    title: "Open the free case",
    summary:
      "Experience the actual briefing format, evidence flow, and tone of a live Cold Case file before you activate full membership.",
    checkpoint: "Open Case File 000 and inspect the free investigation.",
    secondaryActionLabel: "Open Free Case",
  },
  {
    key: "archive_preview",
    eyebrow: "Step 3",
    title: "Preview the archive briefing",
    summary:
      "Watch the first archive briefing. Actual archives are only available for Active Paid Detectives.",
    checkpoint: "Watch the first archive briefing. Actual archives are only available for Active Paid Detectives.",
    primaryActionLabel: "Open Archive Briefing 01",
    primaryActionSource: "registered_demo_archive_video",
  },
  {
    key: "activation",
    eyebrow: "Step 4",
    title: "Activate the Agency",
    summary:
      "Full membership unlocks monthly case files, weekly archive episodes, rank progression, and guest case sharing for your active account.",
    checkpoint: "Convert this intake file into a live member record.",
    primaryActionLabel: "Become An Active Member",
    primaryActionSource: "registered_demo_activation_primary",
  },
] as const;
const REGISTERED_DEMO_STEP_INDEX_BY_KEY = Object.fromEntries(
  REGISTERED_DEMO_STEPS.map((step, index) => [step.key, index])
) as Record<string, number>;

const parseDateSafe = (value: any) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfUtcDay = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const getDaysInUtcMonth = (year: number, monthIndex: number) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

const addMonthsClampedUtc = (date: Date, months: number) => {
  const normalizedMonths = Number(months || 0);
  const targetMonthIndex = date.getUTCMonth() + normalizedMonths;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
  const wrappedMonthIndex = ((targetMonthIndex % 12) + 12) % 12;
  const targetDay = Math.min(
    date.getUTCDate(),
    getDaysInUtcMonth(targetYear, wrappedMonthIndex)
  );

  return new Date(
    Date.UTC(
      targetYear,
      wrappedMonthIndex,
      targetDay,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  );
};

const diffCalendarDays = (targetDate: Date, fromDate = new Date()) => {
  const diffMs = startOfUtcDay(targetDate).getTime() - startOfUtcDay(fromDate).getTime();
  return Math.max(0, Math.round(diffMs / DAY_IN_MS));
};

const getNextQuarterStartDate = (referenceDate: Date) => {
  const currentYear = referenceDate.getUTCFullYear();
  const quarterStartMonths = [0, 3, 6, 9];
  const referenceDay = startOfUtcDay(referenceDate).getTime();

  for (const monthIndex of quarterStartMonths) {
    const candidate = new Date(Date.UTC(currentYear, monthIndex, 1));
    if (candidate.getTime() >= referenceDay) return candidate;
  }

  return new Date(Date.UTC(currentYear + 1, 0, 1));
};

const formatDaysLabel = (days: number) => {
  if (days <= 0) return "Today";
  if (days === 1) return "1 Day";
  return `${days} Days`;
};

const formatTimelineUnlockSummary = ({
  subject,
  date,
  daysUntil,
  pendingMessage = "",
}: {
  subject: string;
  date: unknown;
  daysUntil: number | null;
  pendingMessage?: string;
}) => {
  const dateLabel = formatDisplayDate(date);
  if (!dateLabel || daysUntil === null || Number.isNaN(daysUntil)) {
    return pendingMessage || `${subject} timing will update after the next recorded payment cycle.`;
  }
  if (daysUntil <= 0) {
    return `${subject} is ready now.`;
  }
  return `${subject} unlocks on ${dateLabel} in ${formatDaysLabel(daysUntil).toLowerCase()}.`;
};

const formatStatusLabel = (value: unknown) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "Unknown";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDisplayDate = (value: unknown) => {
  const parsed = parseDateSafe(value);
  if (!parsed) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
};

const formatCustomOrderDate = (value: unknown) => {
  const parsed = parseDateSafe(value);
  if (!parsed) return "Not yet";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const getCustomOrderStatusClasses = (status = "") => {
  switch (String(status || "").trim().toLowerCase()) {
    case "review_required":
      return "border-amber-300/30 bg-amber-300/10 text-amber-100";
    case "delivery_ready":
    case "delivered":
      return "border-emerald-300/30 bg-emerald-300/10 text-emerald-100";
    case "generation_failed":
      return "border-rose-300/30 bg-rose-300/10 text-rose-100";
    default:
      return "border-white/10 bg-white/5 text-[#d7d0c1]";
  }
};

const canMemberHostGroupSessions = (member: any) => {
  const status = String(member?.status || "").trim().toLowerCase();
  if (!member || !status) return false;
  return status === "active";
};

const formatPaidCaseFileNumber = (caseIndex: unknown) => {
  const normalizedCaseIndex = Number(caseIndex || 0);
  if (!Number.isFinite(normalizedCaseIndex) || normalizedCaseIndex < 1001) return "000";
  return String(normalizedCaseIndex - 1000).padStart(3, "0");
};

const getNextPaidCaseIndex = (lastGrantedCaseIndex: unknown) => {
  const normalizedLastGranted = Math.max(0, Number(lastGrantedCaseIndex || 0));
  if (normalizedLastGranted >= 1001) return normalizedLastGranted + 1;
  return 1001;
};

const slugify = (value: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildAgencyArchiveEpisodePath = (
  archiveNumber: number,
  episodeNumber: number,
  title = ""
) => {
  const slug = slugify(title);
  return slug
    ? `/agency/archive/${archiveNumber}/episodes/${episodeNumber}/${slug}`
    : `/agency/archive/${archiveNumber}/episodes/${episodeNumber}`;
};

const formatArchiveHeadingTitle = (value: unknown) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Archive unavailable";
  return normalized.replace(/\s*-\s*Episode\s+\d+\s*$/i, "").trim() || normalized;
};

const formatArchiveEpisodeTag = (episodeNumber: unknown) =>
  `Episode ${String(Number(episodeNumber || 0)).padStart(2, "0")}`;

const formatArchiveEpisodeTitle = (value: unknown) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.replace(/^Episode\s+\d+\s*:\s*/i, "").trim() || normalized;
};

const toEmbedUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  const shortMatch = value.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  const vimeoMatch = value.match(/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:$|[/?#])/i);
  if (vimeoMatch?.[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return value;
};

const isRegisteredOnlyMemberRecord = (clubMember: any) =>
  String(clubMember?.status || "").trim().toLowerCase() === "cancelled" &&
  Number(clubMember?.paidMonthsCount || 0) === 0 &&
  Number(clubMember?.lastGrantedCaseIndex || 0) === 0;

const hasRequestedTrialCase = (clubMember: any) =>
  Boolean(clubMember?.trialRequestedAt) || Number(clubMember?.trialRequestCount || 0) > 0;

const getArchiveEpisodesPerPage = () => {
  if (typeof window === "undefined") return DESKTOP_ARCHIVE_EPISODES_PER_PAGE;
  return window.innerWidth < 768 ? MOBILE_ARCHIVE_EPISODES_PER_PAGE : DESKTOP_ARCHIVE_EPISODES_PER_PAGE;
};

const getDashboardArchivePositionFromSequence = (sequenceNumber: number, archiveDefinitions: any[] = []) => {
  let remaining = Number(sequenceNumber || 0);
  if (!Number.isInteger(remaining) || remaining < 1) return null;

  let traversed = 0;
  for (const archive of archiveDefinitions) {
    const plannedEpisodeCount = Number(archive?.plannedEpisodeCount || 0);
    if (!Number.isInteger(plannedEpisodeCount) || plannedEpisodeCount < 1) continue;

    if (remaining <= plannedEpisodeCount) {
      return {
        archiveNumber: Number(archive?.archiveNumber || 0),
        archiveTitle: String(archive?.title || `Archive ${archive?.archiveNumber || ""}`).trim(),
        episodeNumber: remaining,
        sequenceNumber,
        isAuthored: remaining <= Number(archive?.authoredEpisodeCount || 0),
      };
    }

    remaining -= plannedEpisodeCount;
    traversed += plannedEpisodeCount;
  }

  const lastArchive = archiveDefinitions[archiveDefinitions.length - 1];
  if (!lastArchive) return null;

  return {
    archiveNumber: Number(lastArchive?.archiveNumber || 0) + 1,
    archiveTitle: `Archive ${Number(lastArchive?.archiveNumber || 0) + 1}`,
    episodeNumber: sequenceNumber - traversed,
    sequenceNumber,
    isAuthored: false,
  };
};

const buildDashboardArchiveEpisodes = (timeline: any) => {
  const unlockedEpisodes = Array.isArray(timeline?.unlockedEpisodes) ? timeline.unlockedEpisodes : [];

  const authoredUnlockedEpisodes =
    unlockedEpisodes.length > 0
      ? unlockedEpisodes.map((episode: any) => ({
          ...episode,
          label: `Archive ${Number(episode.archiveNumber || 0)} Episode ${String(
            Number(episode.episodeNumber || 0)
          ).padStart(2, "0")}`,
          statusLabel:
            Number(episode.sequenceNumber || 0) === Number(timeline?.unlockedEpisodeCount || 0)
              ? "Current"
              : "Unlocked",
        }))
      : (() => {
          const unlockedEpisodeCount = Number(timeline?.unlockedEpisodeCount || 0);
          if (!Number.isInteger(unlockedEpisodeCount) || unlockedEpisodeCount < 1) return [];

          const archiveDefinitions = Array.isArray(timeline?.archiveDefinitions)
            ? timeline.archiveDefinitions
            : [];

          const synthesizedEpisodes = [];
          for (let sequenceNumber = 1; sequenceNumber <= unlockedEpisodeCount; sequenceNumber += 1) {
            const episode = getDashboardArchivePositionFromSequence(sequenceNumber, archiveDefinitions);
            if (!episode?.isAuthored) continue;

            const isCurrentSequence = sequenceNumber === unlockedEpisodeCount;
            const currentEpisodeMatches =
              isCurrentSequence &&
              Number(timeline?.currentEpisode?.archiveNumber || 0) === Number(episode.archiveNumber || 0) &&
              Number(timeline?.currentEpisode?.episodeNumber || 0) === Number(episode.episodeNumber || 0);
            const currentEpisode = currentEpisodeMatches ? timeline?.currentEpisode : null;

            synthesizedEpisodes.push({
              ...episode,
              title: String(currentEpisode?.title || "").trim(),
              imageUrl: String(currentEpisode?.imageUrl || "").trim(),
              label: `Archive ${Number(episode.archiveNumber || 0)} Episode ${String(
                Number(episode.episodeNumber || 0)
              ).padStart(2, "0")}`,
              statusLabel: isCurrentSequence ? "Current" : "Unlocked",
            });
          }

          return synthesizedEpisodes;
        })();
  if (authoredUnlockedEpisodes.length === 0) {
    return [];
  }

  const currentEpisodeIndex = authoredUnlockedEpisodes.findIndex(
    (episode: any) => Number(episode.sequenceNumber || 0) === Number(timeline?.unlockedEpisodeCount || 0)
  );
  const orderedUnlockedEpisodes =
    currentEpisodeIndex > 0
      ? [
          authoredUnlockedEpisodes[currentEpisodeIndex],
          ...authoredUnlockedEpisodes.slice(0, currentEpisodeIndex),
          ...authoredUnlockedEpisodes.slice(currentEpisodeIndex + 1),
        ]
      : authoredUnlockedEpisodes;

  return orderedUnlockedEpisodes;
};

const buildDashboardArchiveGroups = (episodes: any[], archiveDefinitions: any[] = [], timeline: any = {}) => {
  const archiveTitleByNumber = new Map<number, string>();
  const archiveDefinitionByNumber = new Map<number, any>();
  (Array.isArray(archiveDefinitions) ? archiveDefinitions : []).forEach((entry: any) => {
    const archiveNumber = Number(entry?.archiveNumber || 0);
    if (!Number.isInteger(archiveNumber) || archiveNumber < 1) return;
    const archiveTitle = String(entry?.title || "").trim();
    if (archiveTitle) archiveTitleByNumber.set(archiveNumber, archiveTitle);
    archiveDefinitionByNumber.set(archiveNumber, entry);
  });

  const groups = new Map<number, any>();
  archiveDefinitionByNumber.forEach((entry, archiveNumber) => {
    groups.set(archiveNumber, {
      archiveNumber,
      archiveTitle:
        String(entry?.title || "").trim() ||
        archiveTitleByNumber.get(archiveNumber) ||
        `Archive ${archiveNumber}`,
      plannedEpisodeCount: Number(entry?.plannedEpisodeCount || 0),
      authoredEpisodeCount: Number(entry?.authoredEpisodeCount || 0),
      unlockedEpisodes: [],
      currentEpisode: null,
      latestEpisode: null,
    });
  });

  (Array.isArray(episodes) ? episodes : []).forEach((episode: any) => {
    const archiveNumber = Number(episode?.archiveNumber || 0);
    if (!Number.isInteger(archiveNumber) || archiveNumber < 1) return;

    if (!groups.has(archiveNumber)) {
      groups.set(archiveNumber, {
        archiveNumber,
        archiveTitle:
          String(episode?.archiveTitle || "").trim() ||
          archiveTitleByNumber.get(archiveNumber) ||
          `Archive ${archiveNumber}`,
        plannedEpisodeCount: Number(archiveDefinitionByNumber.get(archiveNumber)?.plannedEpisodeCount || 0),
        authoredEpisodeCount: Number(archiveDefinitionByNumber.get(archiveNumber)?.authoredEpisodeCount || 0),
        unlockedEpisodes: [],
        currentEpisode: null,
        latestEpisode: null,
      });
    }

    const group = groups.get(archiveNumber);
    group.unlockedEpisodes.push(episode);
    group.latestEpisode =
      !group.latestEpisode ||
      Number(episode?.sequenceNumber || 0) > Number(group.latestEpisode?.sequenceNumber || 0)
        ? episode
        : group.latestEpisode;
    if (String(episode?.statusLabel || "").toLowerCase() === "current") {
      group.currentEpisode = episode;
    }
  });

  return Array.from(groups.values())
    .map((group) => {
      const targetEpisode = group.currentEpisode || group.latestEpisode || null;
      const targetEpisodeNumber = Number(targetEpisode?.episodeNumber || 1);
      const baseArchiveTitle = String(group.archiveTitle || "").trim() || `Archive ${group.archiveNumber}`;
      return {
        ...group,
        unlockedCount: group.unlockedEpisodes.length,
        episodes: [...group.unlockedEpisodes].sort(
          (left: any, right: any) => Number(right?.episodeNumber || 0) - Number(left?.episodeNumber || 0)
        ),
        targetEpisode,
        targetEpisodeTitle:
          String(targetEpisode?.title || "").trim() ||
          `${baseArchiveTitle} - Episode ${targetEpisodeNumber}`,
        currentArchive: group.archiveNumber === Number(timeline?.currentArchiveNumber || 0),
        openPath: targetEpisode
          ? buildAgencyArchiveEpisodePath(
              Number(targetEpisode.archiveNumber || 0),
              Number(targetEpisode.episodeNumber || 0),
              String(targetEpisode.title || targetEpisode.label || "")
            )
          : "",
        nextUnlockLabel:
          group.archiveNumber === Number(timeline?.currentArchiveNumber || 0)
            ? String(timeline?.nextArchiveUnlockLabel || "").trim()
            : "",
      };
    })
    .filter((group) => Number(group.unlockedCount || 0) > 0)
    .sort((left, right) => left.archiveNumber - right.archiveNumber);
};

const getCaseBackgroundImageFromPayload = (payload: any, imageAccessMap: Record<string, string> = {}) => {
  const evidenceFiles = Array.isArray(payload?.evidence_files) ? payload.evidence_files : [];
  const candidates = evidenceFiles.filter(
    (entry: any) => String(entry?.type || "").trim().toLowerCase() === "case_background"
  );
  const target = candidates[0] || evidenceFiles[0];
  if (!target) return "";

  const normalizedImages = normalizeEvidenceImageRecords(
    Array.isArray(target?.images) ? target.images : []
  );
  for (const image of normalizedImages) {
    const direct = String((image as any)?.url || "").trim();
    if (direct) return direct;
    const storageRef = String(image?.storageKey || "").trim();
    if (isHttpAssetUrl(storageRef)) return storageRef;
    const signed = String(imageAccessMap[storageRef] || "").trim();
    if (signed) return signed;
  }
  return "";
};

const resolveCaseCoverUrls = async (clubCases: any[]) => {
  const nextCoverUrls: Record<number, string> = {};

  await Promise.all(
    (Array.isArray(clubCases) ? clubCases : []).map(async (clubCase) => {
      const caseIndex = Number(clubCase?.caseIndex || 0);
      if (!Number.isInteger(caseIndex) || caseIndex < 1) return;

      const payloadCover = getCaseBackgroundImageFromPayload(clubCase?.casePayload || {});
      if (payloadCover) {
        nextCoverUrls[caseIndex] = payloadCover;
        return;
      }

      const response = await ClubApi.getCaseAccess(caseIndex);
      const fallbackCover = getCaseBackgroundImageFromPayload(
        response?.casePayload || {},
        response?.evidenceImageAccessMap || {}
      );
      if (fallbackCover) nextCoverUrls[caseIndex] = fallbackCover;
    })
  );

  return nextCoverUrls;
};

const ClubDashboardPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [member, setMember] = useState<any>(null);
  const [cases, setCases] = useState<any[]>([]);
  const [archiveState, setArchiveState] = useState<any>(null);
  const [activeCaseCoverUrl, setActiveCaseCoverUrl] = useState("");
  const [assignedCaseCoverUrls, setAssignedCaseCoverUrls] = useState<Record<number, string>>({});
  const [trialCasePreview, setTrialCasePreview] = useState<any>(null);
  const [trialCasePreviewCoverUrl, setTrialCasePreviewCoverUrl] = useState("");
  const [archivePreviewEpisode, setArchivePreviewEpisode] = useState<any>(null);
  const [customOrders, setCustomOrders] = useState<ClubCustomOrderSummary[]>([]);
  const [registeredDemoStep, setRegisteredDemoStep] = useState(0);
  const [isRegisteredDemoExpanded, setIsRegisteredDemoExpanded] = useState(false);
  const [showRegisteredIntakeVideo, setShowRegisteredIntakeVideo] = useState(false);
  const [registeredIntakeVideoSource, setRegisteredIntakeVideoSource] = useState<"hero" | "demo">("hero");
  const [showRegisteredArchiveVideo, setShowRegisteredArchiveVideo] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [rankProgress, setRankProgress] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedArchiveNumber, setSelectedArchiveNumber] = useState<number | null>(null);
  const [archiveEpisodePage, setArchiveEpisodePage] = useState(0);
  const [archiveEpisodesPerPage, setArchiveEpisodesPerPage] = useState(getArchiveEpisodesPerPage);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        devLog("archive", "dashboard_load_started", {
          route: "/agency/dashboard",
          hasUser: Boolean(user?.id),
        });
        const [memberResponse, entitlementResponse, archiveStateResponse, customOrdersResponse] = await Promise.all([
          ClubApi.getMe(),
          ClubApi.getEntitlements(),
          Promise.resolve()
            .then(() => ClubApi.getArchiveState())
            .catch((archiveError: any) => {
              devLog("archive", "dashboard_archive_state_fallback", {
                route: "/agency/dashboard",
                status: archiveError?.response?.status,
                message: String(archiveError?.message || ""),
              });
              return null;
            }),
          Promise.resolve()
            .then(() => ClubApi.getMyCustomOrders({ limit: 3 }))
            .catch((customOrderError: any) => {
              devLog("route", "dashboard_custom_orders_fallback", {
                route: "/agency/dashboard",
                status: customOrderError?.response?.status,
                message: String(customOrderError?.message || ""),
              });
              return null;
            }),
        ]);
        const isRegisteredOnlyMember = isRegisteredOnlyMemberRecord(memberResponse?.member);
        const memberHasRequestedTrialCase = hasRequestedTrialCase(memberResponse?.member);
        const shouldShowTrialRequestWelcome =
          isRegisteredOnlyMember && !memberHasRequestedTrialCase;
        const entitledCases = Array.isArray(entitlementResponse?.cases) ? entitlementResponse.cases : [];
        const nextNewFormatCases = entitledCases
          .filter((clubCase) => isNewFormatCase(clubCase))
          .sort((a, b) => Number(b?.caseIndex || 0) - Number(a?.caseIndex || 0));
        const casesNeedingCoverFetch = nextNewFormatCases.filter((clubCase) => {
          const caseIndex = Number(clubCase?.caseIndex || 0);
          if (!Number.isInteger(caseIndex) || caseIndex < 1) return false;
          return !getCaseBackgroundImageFromPayload(clubCase?.casePayload || {});
        });

        let nextCaseCoverUrls: Record<number, string> = {};
        if (casesNeedingCoverFetch.length) {
          devLog("archive", "dashboard_case_covers_load_started", {
            route: "/agency/dashboard",
            caseIndexes: casesNeedingCoverFetch.map((clubCase) => Number(clubCase?.caseIndex || 0)),
          });
          try {
            nextCaseCoverUrls = await resolveCaseCoverUrls(nextNewFormatCases);
            devLog("archive", "dashboard_case_covers_load_succeeded", {
              route: "/agency/dashboard",
              resolvedCaseIndexes: Object.keys(nextCaseCoverUrls).map((value) => Number(value)),
            });
          } catch (coverError: any) {
            devLog("archive", "dashboard_case_covers_load_failed", {
              route: "/agency/dashboard",
              status: coverError?.response?.status,
              message: String(coverError?.message || ""),
            });
          }
        } else {
          nextCaseCoverUrls = Object.fromEntries(
            nextNewFormatCases
              .map((clubCase) => {
                const caseIndex = Number(clubCase?.caseIndex || 0);
                const coverUrl = getCaseBackgroundImageFromPayload(clubCase?.casePayload || {});
                return Number.isInteger(caseIndex) && caseIndex > 0 && coverUrl
                  ? [caseIndex, coverUrl]
                  : null;
              })
              .filter(Boolean) as Array<[number, string]>
          );
        }

        let nextTrialCasePreview = null;
        let nextTrialCasePreviewCoverUrl = "";
        let nextArchivePreviewEpisode = null;
        if (isRegisteredOnlyMember && memberHasRequestedTrialCase) {
          devLog("route", "dashboard_registered_preview_load_started", {
            route: "/agency/dashboard",
            memberStatus: String(memberResponse?.member?.status || ""),
            trialRequestCount: Number(memberResponse?.member?.trialRequestCount || 0),
          });
          const [trialCaseResponse, archivePreviewResponse] = await Promise.all([
            Promise.resolve()
              .then(() => ClubApi.getCaseAccess(TRIAL_CASE_INDEX))
              .catch((previewError: any) => {
              devLog("route", "dashboard_registered_trial_preview_failed", {
                route: "/agency/dashboard",
                status: previewError?.response?.status,
                message: String(previewError?.message || ""),
              });
              return null;
            }),
            Promise.resolve()
              .then(() => ClubApi.getArchiveEpisode(1, 1))
              .catch((previewError: any) => {
              devLog("route", "dashboard_registered_archive_preview_failed", {
                route: "/agency/dashboard",
                status: previewError?.response?.status,
                message: String(previewError?.message || ""),
              });
              return null;
            }),
          ]);

          if (trialCaseResponse) {
            nextTrialCasePreview = trialCaseResponse;
            nextTrialCasePreviewCoverUrl = getCaseBackgroundImageFromPayload(
              trialCaseResponse?.casePayload || {},
              trialCaseResponse?.evidenceImageAccessMap || {}
            );
          }

          if (archivePreviewResponse?.episode) {
            nextArchivePreviewEpisode = archivePreviewResponse.episode;
          }

          devLog("route", "dashboard_registered_preview_load_finished", {
            route: "/agency/dashboard",
            hasTrialCasePreview: Boolean(nextTrialCasePreview),
            hasArchivePreviewEpisode: Boolean(nextArchivePreviewEpisode),
          });
        }

        setMember({
          ...(memberResponse?.member || {}),
          subscription: memberResponse?.subscription || null,
        });
        setRankProgress(memberResponse?.rankProgress || null);
        setCases(entitledCases);
        setArchiveState(archiveStateResponse?.archiveState || null);
        setCustomOrders(Array.isArray(customOrdersResponse?.entries) ? customOrdersResponse.entries : []);
        setAssignedCaseCoverUrls(nextCaseCoverUrls);
        setActiveCaseCoverUrl(
          String(nextCaseCoverUrls[Number(nextNewFormatCases[0]?.caseIndex || 0)] || "").trim()
        );
        setTrialCasePreview(nextTrialCasePreview);
        setTrialCasePreviewCoverUrl(nextTrialCasePreviewCoverUrl);
        setArchivePreviewEpisode(nextArchivePreviewEpisode);
        setShowWelcome(shouldShowTrialRequestWelcome);
        devLog("archive", "dashboard_load_succeeded", {
          route: "/agency/dashboard",
          memberStatus: String(memberResponse?.member?.status || ""),
          paidMonthsCount: Number(memberResponse?.member?.paidMonthsCount || 0),
          trialRequestCount: Number(memberResponse?.member?.trialRequestCount || 0),
          hasTrialRequest: memberHasRequestedTrialCase,
          showWelcome: shouldShowTrialRequestWelcome,
          entitledCaseCount: entitledCases.length,
          customOrderCount: Array.isArray(customOrdersResponse?.entries) ? customOrdersResponse.entries.length : 0,
          archiveStateSource: archiveStateResponse?.archiveState ? "api" : "unavailable",
        });
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setShowWelcome(true);
          setError("");
          setMember(null);
          setCases([]);
          setArchiveState(null);
          setCustomOrders([]);
          setTrialCasePreview(null);
          setTrialCasePreviewCoverUrl("");
          setArchivePreviewEpisode(null);
          devLog("archive", "dashboard_member_missing", {
            route: "/agency/dashboard",
          });
          return;
        }
        setError(err?.response?.data?.error || "Failed to load agency dashboard");
        setArchiveState(null);
        setCustomOrders([]);
        setTrialCasePreview(null);
        setTrialCasePreviewCoverUrl("");
        setArchivePreviewEpisode(null);
        devLog("archive", "dashboard_load_failed", {
          route: "/agency/dashboard",
          status: err?.response?.status,
          message: String(err?.message || ""),
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [refreshKey]);

  const newFormatCases = useMemo(
    () =>
      cases
        .filter((clubCase) => isNewFormatCase(clubCase))
        .sort((a, b) => Number(b?.caseIndex || 0) - Number(a?.caseIndex || 0)),
    [cases]
  );

  const activeCase = newFormatCases[0] || null;
  const recentCustomOrders = customOrders.slice(0, 3);
  const assignedPastCases = newFormatCases.slice(1);
  const publishedCaseIndexes = useMemo(
    () =>
      newFormatCases
        .map((clubCase) => Number(clubCase?.caseIndex || 0))
        .filter((caseIndex) => Number.isInteger(caseIndex) && caseIndex >= 1001),
    [newFormatCases]
  );
  const missingGrantedCaseIndexes = useMemo(() => {
    const lastGrantedCaseIndex = Number(member?.lastGrantedCaseIndex || 0);
    if (!Number.isInteger(lastGrantedCaseIndex) || lastGrantedCaseIndex < 1001) return [];

    const publishedIndexes = new Set(publishedCaseIndexes);
    const missingIndexes = [];
    for (let caseIndex = 1001; caseIndex <= lastGrantedCaseIndex; caseIndex += 1) {
      if (!publishedIndexes.has(caseIndex)) missingIndexes.push(caseIndex);
    }
    return missingIndexes;
  }, [member?.lastGrantedCaseIndex, publishedCaseIndexes]);

  const greetingName = useMemo(() => {
    const first =
      String(user?.firstName || "").trim() ||
      String(member?.displayName || "").trim().split(" ")[0] ||
      "Detective";
    const last =
      String(user?.lastName || "").trim() ||
      String(member?.displayName || "").trim().split(" ").slice(1).join(" ").trim();
    const rankLabel = RANK_LABELS[member?.rank] || "Detective";
    const solveRankLabel = String(rankProgress?.currentRankLabel || "").trim() || rankLabel;
    return `${solveRankLabel} ${last || first}`.trim();
  }, [member?.displayName, member?.rank, rankProgress?.currentRankLabel, user?.firstName, user?.lastName]);
  const preferredFirstName = useMemo(
    () =>
      String(user?.firstName || "").trim() ||
      String(member?.displayName || "").trim().split(" ")[0] ||
      "Detective",
    [member?.displayName, user?.firstName]
  );

  const isNonMember =
    isRegisteredOnlyMemberRecord(member);
  const memberStatus = String(member?.status || "")
    .trim()
    .toLowerCase();
  const isMembershipActive = memberStatus === "active";
  const isInactiveMember = Boolean(member) && !isNonMember && !isMembershipActive;
  const membershipStatusLabel = formatStatusLabel(member?.status);
  const registeredArchivePreviewEmbedUrl = toEmbedUrl(
    String(
      import.meta.env.VITE_CLUB_REGISTERED_ARCHIVE_PREVIEW_VIDEO_URL ||
        archivePreviewEpisode?.briefingVideoUrl ||
        ""
    )
  );
  const registeredIntakeVideoUrl = toEmbedUrl(
    String(import.meta.env.VITE_CLUB_REGISTERED_INTAKE_VIDEO_URL || "https://www.youtube.com/watch?v=Fynka4Qa7Us")
  );
  const registeredTrialCasePath = buildCasePath(
    TRIAL_CASE_INDEX,
    String(trialCasePreview?.title || "trial case"),
    String(trialCasePreview?.briefingTitle || "")
  );
  const membershipCheckoutPath = "/agency/join?plan=digital";
  const sessionHostingAllowed = canMemberHostGroupSessions(member);
  const buildRegisteredDemoPath = (stepKey: string) =>
    `/agency/dashboard?demo=registered&demoStep=${encodeURIComponent(stepKey)}`;
  const buildRegisteredDemoCasePath = (stepKey = "free_case") =>
    `${registeredTrialCasePath}?demo=registered&demoStep=${encodeURIComponent(stepKey)}`;
  const clearRegisteredDemoRouteState = (source: string) => {
    const searchParams = new URLSearchParams(location.search);
    const hadRegisteredDemoQuery = searchParams.get("demo") === "registered";
    const hadRegisteredDemoStep = searchParams.has("demoStep");
    const hadRegisteredDemoHash = location.hash === "#registered-demo";

    if (!hadRegisteredDemoQuery && !hadRegisteredDemoStep && !hadRegisteredDemoHash) {
      return;
    }

    searchParams.delete("demo");
    searchParams.delete("demoStep");
    const nextSearch = searchParams.toString();

    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
    devLog("route", "dashboard_registered_demo_restore_cleared", {
      route: "/agency/dashboard",
      source,
      hadRegisteredDemoQuery,
      hadRegisteredDemoStep,
      hadRegisteredDemoHash,
      memberStatus,
    });
  };
  const registeredDemoStepCount = REGISTERED_DEMO_STEPS.length;
  const registeredDemoContent = REGISTERED_DEMO_STEPS[
    Math.min(registeredDemoStep, registeredDemoStepCount - 1)
  ];

  const openMembershipCheckout = (source: string) => {
    devLog("route", "dashboard_membership_checkout_requested", {
      route: "/agency/dashboard",
      source,
      memberStatus,
      paidMonthsCount: Number(member?.paidMonthsCount || 0),
      lastGrantedCaseIndex: Number(member?.lastGrantedCaseIndex || 0),
    });
    navigate(membershipCheckoutPath);
  };
  const openRegisteredIntakeVideo = (source: "hero" | "demo") => {
    setRegisteredIntakeVideoSource(source);
    devLog("route", "dashboard_registered_intake_video_opened", {
      route: "/agency/dashboard",
      videoConfigured: Boolean(registeredIntakeVideoUrl),
      memberStatus,
      source,
    });
    setShowRegisteredIntakeVideo(true);
  };
  const closeRegisteredIntakeVideo = () => {
    devLog("route", "dashboard_registered_intake_video_closed", {
      route: "/agency/dashboard",
      memberStatus,
      source: registeredIntakeVideoSource,
    });
    setShowRegisteredIntakeVideo(false);
    setRegisteredIntakeVideoSource("hero");
  };
  const returnToRegisteredFreeCaseStep = () => {
    const freeCaseStepIndex = REGISTERED_DEMO_STEP_INDEX_BY_KEY.free_case ?? 1;
    setShowRegisteredIntakeVideo(false);
    setRegisteredDemoStep(freeCaseStepIndex);
    setIsRegisteredDemoExpanded(true);
    navigate(`${buildRegisteredDemoPath("free_case")}#registered-demo`, { replace: true });
    devLog("route", "dashboard_registered_demo_toggle_changed", {
      route: "/agency/dashboard",
      expanded: true,
      source: "registered_intake_video_return",
      stepIndex: freeCaseStepIndex,
      stepKey: "free_case",
      memberStatus,
    });
    devLog("route", "dashboard_registered_intake_video_returned_to_free_case", {
      route: "/agency/dashboard",
      stepIndex: freeCaseStepIndex,
      memberStatus,
    });
  };
  const openRegisteredArchiveVideo = () => {
    devLog("route", "dashboard_registered_archive_video_opened", {
      route: "/agency/dashboard",
      videoConfigured: Boolean(registeredArchivePreviewEmbedUrl),
      memberStatus,
      archiveTitle: String(archivePreviewEpisode?.title || ""),
    });
    setShowRegisteredArchiveVideo(true);
  };
  const closeRegisteredArchiveVideo = () => {
    devLog("route", "dashboard_registered_archive_video_closed", {
      route: "/agency/dashboard",
      memberStatus,
      archiveTitle: String(archivePreviewEpisode?.title || ""),
    });
    setShowRegisteredArchiveVideo(false);
  };
  const returnToRegisteredActivationStep = () => {
    const activationStepIndex = REGISTERED_DEMO_STEP_INDEX_BY_KEY.activation ?? registeredDemoStepCount - 1;
    setShowRegisteredArchiveVideo(false);
    setRegisteredDemoStep(activationStepIndex);
    setIsRegisteredDemoExpanded(true);
    navigate(`${buildRegisteredDemoPath("activation")}#registered-demo`, { replace: true });
    devLog("route", "dashboard_registered_demo_toggle_changed", {
      route: "/agency/dashboard",
      expanded: true,
      source: "registered_archive_video_return",
      stepIndex: activationStepIndex,
      stepKey: "activation",
      memberStatus,
    });
    devLog("route", "dashboard_registered_archive_video_returned_to_activation", {
      route: "/agency/dashboard",
      stepIndex: activationStepIndex,
      memberStatus,
    });
  };

  const timeline = useMemo(() => {
    const now = new Date();
    const freezeAt =
      String(member?.status || "").toLowerCase() === "active"
        ? now
        : parseDateSafe(member?.updatedAt) || now;
    const nextCaseUnlockDate = parseDateSafe(member?.subscription?.currentPeriodEnd);
    const nextCaseUnlockInDays = nextCaseUnlockDate
      ? diffCalendarDays(nextCaseUnlockDate, now)
      : null;

    const nextQuarterlyDate = getNextQuarterStartDate(freezeAt);
    const nextQuarterlyInDays = diffCalendarDays(nextQuarterlyDate, now);
    const nextRankReviewSummary = rankProgress?.nextRankLabel
      ? `${rankProgress.nextRankLabel} unlocks after ${Number(rankProgress?.solvesToNextRank || 0)} more successful solve${Number(rankProgress?.solvesToNextRank || 0) === 1 ? "" : "s"}.`
      : "You are currently at the top of the solve-based rank ladder.";

    const normalizedTimeline = {
      currentEpisode: archiveState?.currentEpisode || null,
      currentEpisodeLabel: String(archiveState?.currentEpisodeLabel || "Archive unavailable"),
      currentArchiveTitle: String(archiveState?.currentArchiveTitle || "Archive unavailable"),
      currentArchiveNumber: Number(archiveState?.currentArchiveNumber || 1),
      unlockedEpisodeCount: Number(archiveState?.unlockedEpisodeCount || 0),
      scheduledEpisodeCount: Number(archiveState?.scheduledEpisodeCount || 0),
      authoredEpisodeCount: Number(archiveState?.authoredEpisodeCount || 0),
      nextEpisode: archiveState?.nextEpisode || null,
      nextArchiveUnlockInDays: Number(archiveState?.nextUnlockInDays ?? 0),
      nextArchiveUnlockLabel:
        String(archiveState?.nextUnlockLabel || "").trim() || "Unavailable",
      nextArchiveUpdateSummary:
        String(archiveState?.nextUpdateSummary || "").trim() ||
        "Archive progression is unavailable right now.",
      holdsAtAuthoredEdge: Boolean(archiveState?.holdsAtAuthoredEdge),
      unlockedEpisodes: Array.isArray(archiveState?.unlockedEpisodes) ? archiveState.unlockedEpisodes : [],
      archiveDefinitions: Array.isArray(archiveState?.archiveDefinitions) ? archiveState.archiveDefinitions : [],
      nextRankLabel: String(rankProgress?.nextRankLabel || ""),
      nextRankReviewInDays: 0,
      nextRankReviewDate: "",
      nextRankReviewSummary,
      nextCaseUnlockInDays,
      nextCaseUnlockDate: nextCaseUnlockDate ? nextCaseUnlockDate.toISOString() : "",
      nextQuarterlyInDays,
      nextQuarterlyDate: nextQuarterlyDate.toISOString(),
    };
    return {
      ...normalizedTimeline,
      archiveEpisodes: buildDashboardArchiveEpisodes(normalizedTimeline),
    };
  }, [
    archiveState,
    member?.status,
    member?.subscription?.currentPeriodEnd,
    member?.updatedAt,
    rankProgress?.nextRankLabel,
    rankProgress?.solvesToNextRank,
  ]);
  const registeredArchiveHeading = (() => {
    const heading = formatArchiveHeadingTitle(
      String(archivePreviewEpisode?.archiveTitle || timeline.currentArchiveTitle || "Shroud of Darkness")
    );
    if (!heading || heading.toLowerCase() === "archive unavailable") {
      return "Shroud of Darkness Preview";
    }
    return heading;
  })();
  const registeredArchiveEpisodeLabel =
    String(archivePreviewEpisode?.title || "Episode 01").trim() || "Episode 01";
  const nextPaidCaseIndex = useMemo(
    () => getNextPaidCaseIndex(member?.lastGrantedCaseIndex),
    [member?.lastGrantedCaseIndex]
  );
  const displayedArchiveEpisodes = useMemo(
    () =>
      Array.isArray(timeline?.archiveEpisodes)
        ? [...timeline.archiveEpisodes].sort(
            (left: any, right: any) => Number(right?.sequenceNumber || 0) - Number(left?.sequenceNumber || 0)
          )
        : [],
    [timeline?.archiveEpisodes]
  );
  const displayedArchiveGroups = useMemo(
    () => buildDashboardArchiveGroups(displayedArchiveEpisodes, timeline?.archiveDefinitions, timeline),
    [displayedArchiveEpisodes, timeline]
  );
  const archiveEpisodesUseDefinitionFallback = useMemo(
    () =>
      (!Array.isArray(archiveState?.unlockedEpisodes) || archiveState.unlockedEpisodes.length === 0) &&
      Number(timeline?.unlockedEpisodeCount || 0) > 0 &&
      Array.isArray(timeline?.archiveDefinitions) &&
      timeline.archiveDefinitions.length > 0 &&
      displayedArchiveEpisodes.length > 0,
    [
      archiveState?.unlockedEpisodes,
      displayedArchiveEpisodes.length,
      timeline?.archiveDefinitions,
      timeline?.unlockedEpisodeCount,
    ]
  );
  const currentArchiveGroup = useMemo(
    () =>
      displayedArchiveGroups.find(
        (group) => Number(group.archiveNumber || 0) === Number(timeline?.currentArchiveNumber || 0)
      ) || displayedArchiveGroups[0] || null,
    [displayedArchiveGroups, timeline?.currentArchiveNumber]
  );
  const selectedArchiveGroup = useMemo(() => {
    if (displayedArchiveGroups.length === 0) return null;
    return (
      displayedArchiveGroups.find(
        (group) => Number(group.archiveNumber || 0) === Number(selectedArchiveNumber || 0)
      ) ||
      currentArchiveGroup ||
      displayedArchiveGroups[0] ||
      null
    );
  }, [currentArchiveGroup, displayedArchiveGroups, selectedArchiveNumber]);

  const selectedArchiveEpisodePageCount = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(
          Number(selectedArchiveGroup?.episodes?.length || 0) / Math.max(1, archiveEpisodesPerPage)
        )
      ),
    [archiveEpisodesPerPage, selectedArchiveGroup?.episodes?.length]
  );
  const paginatedArchiveEpisodes = useMemo(() => {
    const episodes = Array.isArray(selectedArchiveGroup?.episodes) ? selectedArchiveGroup.episodes : [];
    const startIndex = archiveEpisodePage * archiveEpisodesPerPage;
    return episodes.slice(startIndex, startIndex + archiveEpisodesPerPage);
  }, [archiveEpisodePage, archiveEpisodesPerPage, selectedArchiveGroup?.episodes]);

  useEffect(() => {
    if (displayedArchiveGroups.length === 0) {
      setSelectedArchiveNumber(null);
      return;
    }

    setSelectedArchiveNumber((current) => {
      const currentSelection = Number(current || 0);
      const selectedStillExists = displayedArchiveGroups.some(
        (group) => Number(group.archiveNumber || 0) === currentSelection
      );
      if (currentSelection > 0 && selectedStillExists) {
        return current;
      }
      return (
        Number(currentArchiveGroup?.archiveNumber || displayedArchiveGroups[0]?.archiveNumber || 0) || null
      );
    });
  }, [currentArchiveGroup, displayedArchiveGroups]);

  useEffect(() => {
    setArchiveEpisodePage(0);
  }, [selectedArchiveNumber]);

  useEffect(() => {
    setArchiveEpisodePage((current) =>
      Math.min(current, Math.max(0, selectedArchiveEpisodePageCount - 1))
    );
  }, [selectedArchiveEpisodePageCount]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setArchiveEpisodesPerPage(getArchiveEpisodesPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!member) return;
    devLog("archive", "progression_computed", {
      route: "/agency/dashboard",
      memberStatus: String(member?.status || ""),
      currentEpisode: timeline.currentEpisodeLabel,
      authoredEpisodeCount: timeline.authoredEpisodeCount,
      scheduledEpisodeCount: timeline.scheduledEpisodeCount,
      unlockedEpisodeCount: timeline.unlockedEpisodeCount,
      nextArchiveUnlockLabel: timeline.nextArchiveUnlockLabel,
      nextArchiveUnlockInDays: timeline.nextArchiveUnlockInDays,
      archiveCadenceAnchor: String(member?.createdAt || ""),
      nextCaseUnlockInDays: timeline.nextCaseUnlockInDays,
      nextCaseUnlockDate: String(timeline.nextCaseUnlockDate || ""),
      nextRankLabel: String(timeline.nextRankLabel || ""),
      nextRankReviewInDays: Number(timeline.nextRankReviewInDays || 0),
      nextRankReviewDate: String(timeline.nextRankReviewDate || ""),
      nextCycleCaseIndex: nextPaidCaseIndex,
      nextQuarterlyInDays: timeline.nextQuarterlyInDays,
      nextQuarterlyDate: String(timeline.nextQuarterlyDate || ""),
      holdsAtAuthoredEdge: timeline.holdsAtAuthoredEdge,
      archiveGroupCount: displayedArchiveGroups.length,
      selectedArchiveNumber: Number(selectedArchiveGroup?.archiveNumber || 0),
      source: archiveState ? "api" : "unavailable",
      archiveEpisodesSource: archiveEpisodesUseDefinitionFallback ? "definition_fallback" : "payload",
    });
  }, [
    archiveState,
    archiveEpisodesUseDefinitionFallback,
    displayedArchiveGroups.length,
    member,
    member?.status,
    selectedArchiveGroup?.archiveNumber,
    timeline.authoredEpisodeCount,
    timeline.currentEpisodeLabel,
    timeline.holdsAtAuthoredEdge,
    timeline.nextCaseUnlockDate,
    timeline.nextCaseUnlockInDays,
    nextPaidCaseIndex,
    timeline.nextArchiveUnlockLabel,
    timeline.nextArchiveUnlockInDays,
    timeline.nextQuarterlyDate,
    timeline.nextQuarterlyInDays,
    timeline.scheduledEpisodeCount,
    timeline.unlockedEpisodeCount,
  ]);

  useEffect(() => {
    if (!member || !archiveEpisodesUseDefinitionFallback) return;
    devLog("archive", "dashboard_archive_episode_fallback_used", {
      route: "/agency/dashboard",
      unlockedEpisodeCount: Number(timeline.unlockedEpisodeCount || 0),
      archiveDefinitionCount: Array.isArray(timeline.archiveDefinitions)
        ? timeline.archiveDefinitions.length
        : 0,
      currentArchiveNumber: Number(timeline.currentArchiveNumber || 0),
    });
  }, [
    archiveEpisodesUseDefinitionFallback,
    member,
    timeline.archiveDefinitions,
    timeline.currentArchiveNumber,
    timeline.unlockedEpisodeCount,
  ]);

  useEffect(() => {
    if (!member || !selectedArchiveGroup) return;
    devLog("archive", "dashboard_archive_tab_selected", {
      route: "/agency/dashboard",
      archiveNumber: Number(selectedArchiveGroup.archiveNumber || 0),
      archiveTitle: String(selectedArchiveGroup.archiveTitle || ""),
      unlockedEpisodeCount: Number(selectedArchiveGroup.unlockedCount || 0),
      currentArchive: Boolean(selectedArchiveGroup.currentArchive),
    });
  }, [member, selectedArchiveGroup]);

  useEffect(() => {
    if (!member || !selectedArchiveGroup) return;
    devLog("archive", "dashboard_archive_episode_page_changed", {
      route: "/agency/dashboard",
      archiveNumber: Number(selectedArchiveGroup.archiveNumber || 0),
      archiveTitle: String(selectedArchiveGroup.archiveTitle || ""),
      page: archiveEpisodePage + 1,
      pageCount: selectedArchiveEpisodePageCount,
      episodesPerPage: archiveEpisodesPerPage,
    });
  }, [
    archiveEpisodePage,
    archiveEpisodesPerPage,
    member,
    selectedArchiveEpisodePageCount,
    selectedArchiveGroup,
  ]);

  useEffect(() => {
    if (!member) return;
    devLog("archive", "dashboard_case_progress_resolved", {
      route: "/agency/dashboard",
      lastGrantedCaseIndex: Number(member?.lastGrantedCaseIndex || 0),
      publishedCaseIndexes,
      activeCaseIndex: Number(activeCase?.caseIndex || 0),
      missingGrantedCaseIndexes,
    });
  }, [activeCase?.caseIndex, member, member?.lastGrantedCaseIndex, missingGrantedCaseIndexes, publishedCaseIndexes]);

  useEffect(() => {
    if (!isNonMember) return;

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("demo") !== "registered") return;

    const requestedStepKey = String(searchParams.get("demoStep") || "").trim();
    const nextStepIndex = REGISTERED_DEMO_STEP_INDEX_BY_KEY[requestedStepKey];
    if (!Number.isInteger(nextStepIndex)) return;

    setRegisteredDemoStep(nextStepIndex);
    if (!isRegisteredDemoExpanded) {
      setIsRegisteredDemoExpanded(true);
      devLog("route", "dashboard_registered_demo_toggle_changed", {
        route: "/agency/dashboard",
        expanded: true,
        source: "query_restore",
        stepKey: requestedStepKey,
        stepIndex: nextStepIndex,
        memberStatus,
      });
    }
    devLog("route", "dashboard_registered_demo_step_restored", {
      route: "/agency/dashboard",
      stepKey: requestedStepKey,
      stepIndex: nextStepIndex,
    });
  }, [isNonMember, location.search, memberStatus]);

  useEffect(() => {
    if (!isNonMember) return;
    if (location.hash !== "#registered-demo") return;

    if (!isRegisteredDemoExpanded) {
      setIsRegisteredDemoExpanded(true);
      devLog("route", "dashboard_registered_demo_toggle_changed", {
        route: "/agency/dashboard",
        expanded: true,
        source: "hash_restore",
        stepIndex: registeredDemoStep,
        stepKey: registeredDemoContent?.key || "",
        memberStatus,
      });
    }
  }, [isNonMember, location.hash, memberStatus, registeredDemoContent?.key, registeredDemoStep]);

  useEffect(() => {
    if (!isNonMember) return;
    if (location.hash !== "#registered-demo") return;
    if (!isRegisteredDemoExpanded) return;

    const scrollToRegisteredDemo = () => {
      const target = document.getElementById("registered-demo");
      if (!target) return;
      if (typeof (target as HTMLElement).scrollIntoView !== "function") return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      devLog("route", "dashboard_registered_demo_scrolled", {
        route: "/agency/dashboard",
        hash: location.hash,
      });
    };

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(scrollToRegisteredDemo);
    }
  }, [isNonMember, isRegisteredDemoExpanded, location.hash]);

  useEffect(() => {
    if (!isNonMember) {
      setRegisteredDemoStep(0);
      setIsRegisteredDemoExpanded(false);
      setShowRegisteredIntakeVideo(false);
      setRegisteredIntakeVideoSource("hero");
      setShowRegisteredArchiveVideo(false);
      return;
    }

    devLog("route", "dashboard_registered_demo_step_viewed", {
      route: "/agency/dashboard",
      stepIndex: registeredDemoStep,
      stepKey: registeredDemoContent?.key || "",
      memberStatus,
    });
  }, [isNonMember, memberStatus, registeredDemoContent?.key, registeredDemoStep]);

  useEffect(() => {
    if (!showRegisteredIntakeVideo) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRegisteredIntakeVideo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showRegisteredIntakeVideo]);

  useEffect(() => {
    if (!showRegisteredArchiveVideo) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRegisteredArchiveVideo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showRegisteredArchiveVideo]);

  useEffect(() => {
    if (!member) return;
    devLog("route", "dashboard_membership_state_resolved", {
      route: "/agency/dashboard",
      memberStatus,
      isMembershipActive,
      isInactiveMember,
      isNonMember,
      paidMonthsCount: Number(member?.paidMonthsCount || 0),
      lastGrantedCaseIndex: Number(member?.lastGrantedCaseIndex || 0),
      unlockedEpisodeCount: Number(timeline?.unlockedEpisodeCount || 0),
    });
  }, [
    isInactiveMember,
    isMembershipActive,
    isNonMember,
    member,
    member?.lastGrantedCaseIndex,
    member?.paidMonthsCount,
    memberStatus,
    timeline?.unlockedEpisodeCount,
  ]);

  const toggleRegisteredDemo = () => {
    const nextExpanded = !isRegisteredDemoExpanded;
    if (!nextExpanded) {
      clearRegisteredDemoRouteState("bottom_toggle_close");
    }

    setIsRegisteredDemoExpanded(nextExpanded);
    devLog("route", "dashboard_registered_demo_toggle_changed", {
      route: "/agency/dashboard",
      expanded: nextExpanded,
      source: "bottom_toggle",
      stepIndex: registeredDemoStep,
      stepKey: registeredDemoContent?.key || "",
      memberStatus,
    });
  };

  if (loading) return <div className="px-4 py-5 text-white sm:px-5 sm:py-6">Loading agency dashboard...</div>;
  if (showWelcome) {
    return <ClubWelcomePage onMembershipCreated={() => setRefreshKey((previous) => previous + 1)} />;
  }
  if (error) return <div className="p-6 text-red-400">{error}</div>;

  if (isNonMember) {
    return (
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-4 text-[#eceae4]">
        <section className="mt-6 rounded-3xl border border-[#1d2230] bg-[radial-gradient(circle_at_85%_0%,rgba(101,79,42,0.2),transparent_40%),radial-gradient(circle_at_20%_0%,rgba(44,54,88,0.22),transparent_42%),#090d15] p-7">
          <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Agency Intake Record</p>
          <h1 className="mt-4 text-4xl font-black leading-[0.95] text-[#efede7] sm:text-5xl lg:max-w-[24ch]">
            {preferredFirstName}, welcome to the Agency.
          </h1>
          <p className="mt-5 max-w-3xl text-base text-[#aba69a] sm:text-lg">
          Thank you for registering.
        </p><p className="mt-1 max-w-3xl text-base text-[#aba69a] sm:text-lg">
          Start with the intake video to understand how the Agency operates.
          </p><p className="mt-1 max-w-3xl text-base text-[#aba69a] sm:text-lg"  >
          Then take on your free case to experience the investigation firsthand.
          </p><p className="mt-1 max-w-3xl text-base text-[#aba69a] sm:text-lg">
          Preview the Archive briefing summary to learn about our first 24-episode weekly serial.
          </p><p className="mt-1 max-w-3xl text-base text-[#aba69a] sm:text-lg">
          When you're ready to move from registration into active service, join the Cold Case Agency.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => openRegisteredIntakeVideo("hero")}
              className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#efe7d6] transition hover:bg-white/10"
            >
              Watch Agency Intake Video
            </button>
            <Link
              to={registeredTrialCasePath}
              className="inline-flex items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110"
            >
              Open Free Case
            </Link>
            <button
              type="button"
              onClick={() => openMembershipCheckout("registered_hero_primary")}
              className="inline-flex items-center rounded-full border border-amber-200/40 bg-amber-200/10 px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-amber-50 transition hover:bg-amber-200/15"
            >
              Join Cold Case Agency
            </button>
          </div>
        </section>

        <section className="mt-6 grid items-stretch gap-6 lg:grid-cols-[3fr_2fr]">
          <article className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Free Trial Assignment</p>
              <h2 className="mt-3 text-2xl font-bold text-[#efede7]">
                {trialCasePreview
                  ? `${trialCasePreview?.briefingTitle || trialCasePreview?.title}`
                  : "Free case ready to open"}
              </h2>
              </div>
              <Link
                to={registeredTrialCasePath}
                className="inline-flex w-fit items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#ece1c8] hover:brightness-110"
              >
                Open Free Case
              </Link>
            </div>

            <div className="mt-5 rounded-2xl border border-[#253041] bg-[radial-gradient(circle_at_10%_0%,rgba(55,91,138,0.25),transparent_38%),#0f1521] p-4 sm:p-5">
              {trialCasePreviewCoverUrl ? (
                <div className="relative mb-4 overflow-hidden rounded-xl border border-white/10 bg-black/35">
                  <img
                    src={trialCasePreviewCoverUrl}
                    alt={`${String(trialCasePreview?.briefingTitle || trialCasePreview?.title || "Free case")} cover`}
                    className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
                </div>
              ) : null}
              <h3 className="text-xl font-semibold">Start Here</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#aba69a]">
                {String(
                  trialCasePreview?.summary ||
                    "Open the free case to experience the briefing format, evidence flow, and detective tone before activating full membership."
                ).trim()}
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#c6b894]">
                {preferredFirstName}, this is your first briefing room.
              </p>
            </div>
          </article>

          <article className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
            <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Locked Archive Preview</p>
            <h2 className="mt-4 text-2xl font-bold text-[#efede7]">{registeredArchiveHeading}</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#b2ada1]">
              Each archive includes a briefing. Below is the opening briefing for {registeredArchiveEpisodeLabel}.
            </p>
            <div className="mt-5 overflow-hidden rounded-2xl border border-[#253041] bg-[#0f1521]">
              {registeredArchivePreviewEmbedUrl ? (
                <iframe
                  title="Archive preview video"
                  src={registeredArchivePreviewEmbedUrl}
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <div className="flex aspect-video items-center justify-center px-6 text-center text-sm text-[#aba69a]">
                  The first archive briefing will appear here when a preview video is available.
                </div>
              )}
            </div>
            <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-400/10 p-4">
              <p className="text-[11px] uppercase tracking-[0.24em] text-amber-100">Member Lock</p>
              <p className="mt-2 text-sm leading-relaxed text-[#f0e5ca]">
                Archives are available to Paid Agency Members. Join the Agency to receive weekly archive drops.
              </p>
            </div>
          </article>
        </section>

        <div className="mt-6">
          <button
            type="button"
            onClick={toggleRegisteredDemo}
            aria-expanded={isRegisteredDemoExpanded}
            aria-controls="registered-demo"
            className="inline-flex w-full items-center justify-center rounded-3xl border border-[#30394a] bg-[linear-gradient(180deg,rgba(18,24,36,0.96),rgba(10,15,24,0.96))] px-6 py-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#e8deca] transition hover:border-[#4f5d75] hover:text-white"
          >
            {isRegisteredDemoExpanded ? "Press to Close the Demo Form" : "Press to Open the Demo Form"}
          </button>
        </div>

        {isRegisteredDemoExpanded ? (
          <section
            id="registered-demo"
            className="mt-4 scroll-mt-28 rounded-3xl border border-[#202632] bg-[#0a0f18] p-6"
          >
            <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Walkthrough Demo</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {REGISTERED_DEMO_STEPS.map((step, index) => {
                const isSelected = index === registeredDemoStep;
                return (
                  <button
                    key={step.key}
                    type="button"
                    onClick={() => {
                      setRegisteredDemoStep(index);
                      navigate(buildRegisteredDemoPath(step.key), { replace: true });
                    }}
                    className={`rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition sm:text-xs ${
                      isSelected
                        ? "border-[#6d6558] bg-[linear-gradient(180deg,rgba(58,55,47,0.82),rgba(19,23,31,0.9))] text-[#f4ecda]"
                        : "border-[#2d3442] bg-[#111722] text-[#aeb6c3] hover:border-[#465066] hover:text-[#e8e0cd]"
                    }`}
                  >
                    {step.eyebrow} · {step.title}
                  </button>
                );
              })}
            </div>
            <article className="mt-5 rounded-2xl border border-[#253041] bg-[radial-gradient(circle_at_10%_0%,rgba(55,91,138,0.18),transparent_38%),#0f1521] p-5">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#c6b894]">
                {registeredDemoContent.eyebrow}
              </p>
              <h2 className="mt-3 text-2xl font-bold text-[#efede7]">
                {preferredFirstName}, {registeredDemoContent.title.toLowerCase()}.
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#b2ada1]">
                {registeredDemoContent.summary}
              </p>
              <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#9f9a8e]">Checkpoint</p>
                <p className="mt-2 text-sm text-[#eceae4]">{registeredDemoContent.checkpoint}</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {registeredDemoContent.secondaryActionLabel ? (
                  <Link
                    to={buildRegisteredDemoCasePath(registeredDemoContent.key)}
                    className="inline-flex items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#ece1c8] hover:brightness-110"
                  >
                    {registeredDemoContent.secondaryActionLabel}
                  </Link>
                ) : null}
                {registeredDemoContent.primaryActionLabel ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (registeredDemoContent.key === "welcome") {
                        openRegisteredIntakeVideo("demo");
                        return;
                      }
                      if (registeredDemoContent.key === "archive_preview") {
                        openRegisteredArchiveVideo();
                        return;
                      }
                      openMembershipCheckout(registeredDemoContent.primaryActionSource);
                    }}
                    className="inline-flex items-center rounded-full border border-amber-200/40 bg-amber-200/10 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-amber-50 transition hover:bg-amber-200/15"
                  >
                    {registeredDemoContent.primaryActionLabel}
                  </button>
                ) : null}
              </div>
            </article>
          </section>
        ) : null}
        {showRegisteredIntakeVideo ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(3,6,12,0.82)] px-4 py-8"
            role="dialog"
            aria-modal="true"
            aria-label="Agency intake briefing"
            onClick={closeRegisteredIntakeVideo}
          >
            <div
              className="w-full max-w-4xl rounded-3xl border border-[#2b3241] bg-[#09101a] p-4 shadow-[0_28px_80px_rgba(0,0,0,0.55)] sm:p-5"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#c6b894]">
                    Agency Intake
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-[#efede7]">
                    {preferredFirstName}, welcome to the Agency.
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#b2ada1]">
                    {registeredIntakeVideoSource === "demo"
                      ? "Watch the intake briefing, then return to the demo to open your free case."
                      : "Watch the intake briefing, then close this window when you are ready to continue."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeRegisteredIntakeVideo}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#efe7d6] transition hover:bg-white/10"
                  aria-label="Close intake video"
                >
                  Close
                </button>
              </div>
              {registeredIntakeVideoUrl ? (
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <iframe
                    title="Agency intake briefing video"
                    src={registeredIntakeVideoUrl}
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/20 bg-white/5 p-6 text-sm text-[#b6b1a3]">
                  Add <code>VITE_CLUB_REGISTERED_INTAKE_VIDEO_URL</code> to show the Agency intake briefing here.
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={
                    registeredIntakeVideoSource === "demo"
                      ? returnToRegisteredFreeCaseStep
                      : closeRegisteredIntakeVideo
                  }
                  className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#07111f]"
                >
                  {registeredIntakeVideoSource === "demo" ? "Return to Demo" : "Close"}
                </button>
                <button
                  type="button"
                  onClick={() => openMembershipCheckout("registered_intake_video_checkout")}
                  className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
                >
                  Become An Active Member
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {showRegisteredArchiveVideo ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(3,6,12,0.82)] px-4 py-8"
            role="dialog"
            aria-modal="true"
            aria-label="Archive preview briefing"
            onClick={closeRegisteredArchiveVideo}
          >
            <div
              className="w-full max-w-4xl rounded-3xl border border-[#2b3241] bg-[#09101a] p-4 shadow-[0_28px_80px_rgba(0,0,0,0.55)] sm:p-5"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#c6b894]">
                    Archive Preview
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-[#efede7]">
                    {preferredFirstName}, watch the first archive briefing.
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#b2ada1]">
                    Actual archives are only available for Active Paid Detectives.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeRegisteredArchiveVideo}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#efe7d6] transition hover:bg-white/10"
                  aria-label="Close archive preview video"
                >
                  Close
                </button>
              </div>
              {registeredArchivePreviewEmbedUrl ? (
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <iframe
                    title="Archive preview briefing video"
                    src={registeredArchivePreviewEmbedUrl}
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/20 bg-white/5 p-6 text-sm text-[#b6b1a3]">
                  The first archive briefing will appear here when a preview video is available.
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={returnToRegisteredActivationStep}
                  className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#07111f]"
                >
                  Return to Demo
                </button>
                <button
                  type="button"
                  onClick={() => openMembershipCheckout("registered_archive_video_checkout")}
                  className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
                >
                  Become An Active Member
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  const heroSummary = isInactiveMember
    ? `Your Agency record is currently ${membershipStatusLabel.toLowerCase()}. Previously unlocked case files and archive episodes remain visible here, but new progression is paused until membership is reactivated.`
    : "Your progression is active. Case advancement and archive cadence follow your personal service record.";
  const primaryCardThreeLabel = isInactiveMember ? "Membership Status" : "Next Case Unlock";
  const primaryCardThreeValue = isInactiveMember
    ? membershipStatusLabel
    : timeline.nextCaseUnlockInDays === null
      ? "Pending"
      : formatDaysLabel(timeline.nextCaseUnlockInDays);
  const primaryCardFourLabel = isInactiveMember ? "Current Archive Access" : "Next Archive Unlock";
  const primaryCardFourValue = isInactiveMember
    ? timeline.currentEpisodeLabel
    : timeline.nextArchiveUnlockLabel;
  const activeCaseSectionLabel = isInactiveMember ? "Latest Unlocked Case" : "Active Investigation";
  const activeCaseSummary = activeCase?.summary
    ? activeCase.summary
    : isInactiveMember
      ? "Your previously unlocked case file remains available here. Reactivate membership to resume new assignments and ongoing progression."
      : "Your next assignment will appear here as soon as a case is unlocked.";
  const timelineEyebrow = isInactiveMember ? "Membership Status" : "Upcoming Events";
  const timelineHeading = isInactiveMember ? "Access is paused" : "What Happens Next";
  const inactiveStatusSummary =
    memberStatus === "past_due"
      ? "Billing needs attention before new case files and archive progression can resume."
      : "New case files and new archive progression are paused until membership is reactivated.";

  return (
    <section className="mx-auto max-w-6xl px-4 pb-10 pt-4 text-[#eceae4]">
      <section id="dashboard" className="mt-6 rounded-3xl border border-[#1d2230] bg-[radial-gradient(circle_at_85%_0%,rgba(101,79,42,0.2),transparent_40%),radial-gradient(circle_at_20%_0%,rgba(44,54,88,0.22),transparent_42%),#090d15] p-7">
        <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Agency Member Record</p>
        <h1 className="mt-4 text-4xl font-black leading-[0.95] text-[#efede7] sm:text-5xl lg:max-w-[18ch]">
          Welcome back, {greetingName}.
        </h1>
        <p className="mt-5 text-base text-[#aba69a] sm:text-lg">
          {heroSummary}
        </p>
        {isInactiveMember ? (
          <div className="mt-6 flex flex-wrap gap-3 rounded-2xl border border-amber-300/25 bg-amber-300/10 p-4">
            <div className="min-w-[14rem] flex-1">
              <p className="text-[11px] uppercase tracking-[0.26em] text-amber-100">Progression Paused</p>
              <p className="mt-2 text-sm leading-relaxed text-[#f0e5ca]">
                {inactiveStatusSummary}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => openMembershipCheckout("inactive_hero_primary")}
                className="inline-flex items-center rounded-full border border-amber-200/40 bg-amber-200/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-50 transition hover:bg-amber-200/15"
              >
                View Membership Options
              </button>
              <Link
                to="/agency/settings"
                className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#efe7d6] transition hover:bg-white/10"
              >
                Review Account Status
              </Link>
            </div>
          </div>
        ) : null}
        <div className="mt-7 grid gap-4 lg:grid-cols-4">
          <article className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <div className="flex items-start gap-3">
              <RankBadge
                rankKey={String(rankProgress?.currentRankKey || member?.rank || "")}
                rankLabel={String(rankProgress?.currentRankLabel || "").trim() || RANK_LABELS[member?.rank] || "Junior Detective"}
                size={56}
                className="mt-1 h-14 w-14 rounded-2xl border border-amber-200/10 bg-white/5 p-1.5"
              />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">Current Rank</p>
                <p className="mt-2 text-2xl font-semibold">
                  {String(rankProgress?.currentRankLabel || "").trim() || RANK_LABELS[member?.rank] || "Junior Detective"}
                </p>
              </div>
            </div>
          </article>
          <article className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">Successful Solves</p>
            <p className="mt-2 text-2xl font-semibold">{Number(rankProgress?.successfulSolvesCount || member?.successfulSolvesCount || 0)}</p>
          </article>
          <article className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">{primaryCardThreeLabel}</p>
            <p className="mt-2 text-2xl font-semibold">{primaryCardThreeValue}</p>
          </article>
          <article className="rounded-2xl border border-[#2b303c] bg-[#101520]/55 p-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">{primaryCardFourLabel}</p>
            <p className="mt-2 text-2xl font-semibold">{primaryCardFourValue}</p>
          </article>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Custom Story Desk</p>
            <h2 className="mt-3 text-2xl font-bold text-[#efede7]">Your latest storefront custom stories</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#aba69a]">
              Resume intake, monitor the build, or reopen finished stories tied to your member email.
            </p>
          </div>
          <Link
            to="/agency/custom-cases"
            className="inline-flex w-fit items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#ece1c8] hover:brightness-110"
          >
            View All Custom Stories
          </Link>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          {recentCustomOrders.length === 0 ? (
            <div className="rounded-2xl border border-[#272d38] bg-[#111622]/65 p-5 text-sm text-[#b6b1a3] lg:col-span-3">
              No custom stories are tied to this member email yet.
            </div>
          ) : (
            recentCustomOrders.map((entry) => (
              <article
                key={entry.id}
                className="rounded-2xl border border-[#272d38] bg-[radial-gradient(circle_at_0%_0%,rgba(38,51,76,0.16),transparent_36%),#101520] p-5"
              >
                {entry.coverImageUrl ? (
                  <div className="relative mb-4 overflow-hidden rounded-xl border border-white/10 bg-black/35">
                    <img
                      src={entry.coverImageUrl}
                      alt={`${entry.title} cover`}
                      className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getCustomOrderStatusClasses(entry.status)}`}>
                    {entry.status.replace(/_/g, " ")}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-[#b6b1a3]">
                    {entry.suspectCount} suspect{entry.suspectCount === 1 ? "" : "s"}
                  </span>
                  {entry.language ? (
                    <span className="rounded-full border border-[#3b475c] bg-[#111a28] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#c6d5ec]">
                      {entry.language}
                    </span>
                  ) : null}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-[#efede7]">{entry.title}</h3>
                <p className="mt-2 text-sm text-[#aba69a]">{entry.lifecycleSummary}</p>
                <dl className="mt-4 grid gap-3 text-sm text-[#d7d0c1]">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">City</dt>
                    <dd className="mt-1">{entry.city || "Not set"}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Updated</dt>
                    <dd className="mt-1">{formatCustomOrderDate(entry.updatedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Language</dt>
                    <dd className="mt-1">{entry.language || "Not set"}</dd>
                  </div>
                </dl>
                <div className="mt-5">
                  <Link
                    to={entry.openIntakePath}
                    className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#efe7d6] transition hover:bg-white/10"
                  >
                    Open Story
                  </Link>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="mt-6 grid items-stretch gap-6 lg:grid-cols-[3fr_2fr]">
        <article className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">{activeCaseSectionLabel}</p>
              <h2 className="mt-3 text-2xl font-bold text-[#efede7]">
                {activeCase
                  ? `Case File ${formatPaidCaseFileNumber(activeCase.caseIndex)} — ${activeCase.briefingTitle || activeCase.title}`
                  : "No Active Case"}
              </h2>
            </div>

            {activeCase && sessionHostingAllowed ? (
              <div className="inline-flex items-center gap-2 self-start sm:self-auto">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#b6b1a3]">Field Group Operations</p>
                <Link
                  to={`/agency/sessions/new?caseIndex=${encodeURIComponent(
                    String(activeCase.caseIndex)
                  )}&caseName=${encodeURIComponent(
                    String(activeCase.briefingTitle || activeCase.title || "")
                  )}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-300/40 bg-cyan-400/10 text-cyan-100 transition hover:bg-cyan-400/20"
                  title="Press to start a group sharing sessions with your friends."
                  aria-label="Start Group Session"
                >
                  <Users className="h-4 w-4" />
                </Link>
              </div>
            ) : null}
          </div>

          <div className="mt-5 rounded-2xl border border-[#253041] bg-[radial-gradient(circle_at_10%_0%,rgba(55,91,138,0.25),transparent_38%),#0f1521] p-4 sm:p-5">
            <div>
              {activeCaseCoverUrl ? (
                <div className="relative mb-4 overflow-hidden rounded-xl border border-white/10 bg-black/35">
                  <img
                    src={activeCaseCoverUrl}
                    alt={`${String(activeCase?.briefingTitle || activeCase?.title || "Case file")} cover`}
                    className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
                </div>
              ) : null}
              <h3 className="text-xl font-semibold">Current Assignment</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#aba69a]">
                {activeCaseSummary}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
              {activeCase ? (
                <Link
                  to={buildCasePath(activeCase.caseIndex, activeCase.title, activeCase.briefingTitle)}
                  className="inline-flex w-fit items-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110"
                >
                  Continue Investigation
                </Link>
              ) : null}
            </div>
          </div>
        </article>

        <article className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-6">
          <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">{timelineEyebrow}</p>
          <h2 className="mt-4 text-2xl font-bold text-[#efede7]">{timelineHeading}</h2>
          <ul className="mt-6 divide-y divide-[#1f2430] border-t border-[#1f2430]">
            {isInactiveMember ? (
              <>
                <li className="py-4 text-base text-[#b2ada1]">{inactiveStatusSummary}</li>
                <li className="py-4 text-base text-[#b2ada1]">
                  Current archive access remains at {timeline.currentEpisodeLabel}.
                </li>
                <li className="py-4 text-base text-[#b2ada1]">
                  {activeCase
                    ? `Your latest granted case file remains Case File ${formatPaidCaseFileNumber(activeCase.caseIndex)}.`
                    : "Previously granted case files remain available in your case library."}
                </li>
                <li className="py-4 text-base text-[#b2ada1]">
                  Review account status in settings or return to the Agency join page to reactivate full progression.
                </li>
              </>
            ) : (
              <>
                <li className="py-4 text-base text-[#b2ada1]">
                  {timeline.nextArchiveUpdateSummary}
                </li>
                <li className="py-4 text-base text-[#b2ada1]">
                  {formatTimelineUnlockSummary({
                    subject: `Case File ${formatPaidCaseFileNumber(nextPaidCaseIndex)}`,
                    date: timeline.nextCaseUnlockDate,
                    daysUntil:
                      timeline.nextCaseUnlockInDays === null
                        ? null
                        : Number(timeline.nextCaseUnlockInDays || 0),
                    pendingMessage:
                      "Next case unlock timing will appear when the current paid cycle is recorded on the member subscription.",
                  })}
                </li>
                <li className="py-4 text-base text-[#b2ada1]">
                  {timeline.nextRankReviewSummary}
                </li>
                <li className="py-4 text-base text-[#b2ada1]">
                  {formatTimelineUnlockSummary({
                    subject: "Quarterly common briefing",
                    date: timeline.nextQuarterlyDate,
                    daysUntil: Number(timeline.nextQuarterlyInDays || 0),
                  })}
                </li>
              </>
            )}
          </ul>
        </article>
      </section>

      <section className="mt-6 grid items-stretch gap-6 lg:grid-cols-[3fr_2fr]">
        <article id="cases" className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-4 sm:p-6">
          <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Assigned Cases</p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xl font-bold text-[#efede7] sm:text-2xl">Case File Library</h2>
            <div className="flex flex-wrap items-center gap-3">
              {isInactiveMember ? (
                <button
                  type="button"
                  onClick={() => openMembershipCheckout("inactive_case_library")}
                  className="inline-flex items-center rounded-full border border-amber-200/35 bg-amber-200/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-50 transition hover:bg-amber-200/15 sm:px-4 sm:text-[11px] sm:tracking-[0.18em]"
                >
                  Rejoin Agency
                </button>
              ) : null}
              {sessionHostingAllowed ? (
                <Link
                  to="/agency/sessions/manage"
                  className="inline-flex items-center gap-2 rounded-full border border-[#2a4f6b] bg-[linear-gradient(180deg,rgba(31,84,112,0.32),rgba(16,38,56,0.52))] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#d8f4fb] transition hover:border-[#4f86a8] hover:bg-[linear-gradient(180deg,rgba(54,120,154,0.34),rgba(18,43,62,0.68))] sm:px-4 sm:text-[11px] sm:tracking-[0.18em]"
                >
                  <Users className="h-4 w-4" />
                  Manage Field Operations
                </Link>
              ) : null}
            </div>
          </div>
          <ul className="mt-6 max-h-[22rem] space-y-3 overflow-y-auto pr-1">
            {assignedPastCases.length === 0 ? (
              <li className="rounded-2xl border border-[#272d38] bg-[#111622]/65 p-4 text-base text-[#a8a396] sm:text-lg">
                No previous assigned cases yet.
              </li>
            ) : (
              assignedPastCases.map((clubCase) => (
                (() => {
                  const caseIndex = Number(clubCase?.caseIndex || 0);
                  const caseImageUrl =
                    getCaseBackgroundImageFromPayload(clubCase?.casePayload || {}) ||
                    String(assignedCaseCoverUrls[caseIndex] || "").trim();

                  return (
                    <li
                      key={clubCase.caseIndex}
                      className="relative overflow-hidden rounded-2xl border border-[#272d38] bg-[#111622]/65"
                    >
                      {caseImageUrl ? (
                        <>
                          <div
                            data-testid={`assigned-case-bg-${clubCase.caseIndex}`}
                            className="absolute inset-0 bg-cover bg-center opacity-55 saturate-[0.78]"
                            style={{ backgroundImage: `url("${caseImageUrl}")` }}
                          />
                          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(5,10,18,0.86),rgba(7,12,21,0.76)_45%,rgba(5,10,18,0.88))]" />
                          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,8,14,0.08),rgba(4,8,14,0.38))]" />
                        </>
                      ) : null}
                      <div className="relative flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                        <p className="text-base text-[#eceae4] sm:text-lg">
                          Case File {formatPaidCaseFileNumber(clubCase.caseIndex)} - {clubCase.briefingTitle || clubCase.title}
                        </p>
                        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
                          {sessionHostingAllowed ? (
                            <Link
                              to={`/agency/sessions/new?caseIndex=${encodeURIComponent(
                                String(clubCase.caseIndex)
                              )}&caseName=${encodeURIComponent(
                                String(clubCase.briefingTitle || clubCase.title || "")
                              )}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-300/40 bg-cyan-400/10 text-cyan-100 transition hover:bg-cyan-400/20"
                              title="Press to start a group sharing sessions with your friends."
                              aria-label="Start Group Session"
                            >
                              <Users className="h-4 w-4" />
                            </Link>
                          ) : null}
                          <Link
                            to={buildCasePath(clubCase.caseIndex, clubCase.title, clubCase.briefingTitle)}
                            className="inline-flex items-center gap-2 rounded-full border border-[#353b49] bg-[rgba(9,15,24,0.42)] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#d5cec0] hover:bg-white/5 sm:px-4 sm:text-xs sm:tracking-[0.16em]"
                          >
                            <FolderOpen className="h-4 w-4" />
                            Open
                          </Link>
                        </div>
                      </div>
                    </li>
                  );
                })()
              ))
            )}
          </ul>
        </article>

        <article id="archive" className="h-full rounded-3xl border border-[#202632] bg-[#0a0f18] p-4 sm:p-6">
          <p className="text-xs uppercase tracking-[0.32em] text-[#c6b894]">Weekly Archive</p>
          <h2 className="mt-4 text-xl font-bold text-[#efede7] sm:text-2xl">
            {formatArchiveHeadingTitle(timeline.currentArchiveTitle)}
          </h2>
          <p className="mt-3 text-xs leading-relaxed text-[#a8a396] sm:text-sm">
            Current access: {timeline.currentEpisodeLabel}. The current archive stays featured here,
            and the archive browser below lets you jump directly into any unlocked episode as new
            investigations are added.
          </p>
          {currentArchiveGroup ? (
            <div className="relative mt-6 overflow-hidden rounded-[1.6rem] border border-[#2a3140] bg-[#111622]/80 shadow-[0_24px_60px_rgba(0,0,0,0.34)]">
              {String((currentArchiveGroup.targetEpisode as any)?.imageUrl || "").trim() ? (
                <>
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-60 saturate-[0.78]"
                    style={{
                      backgroundImage: `url("${String((currentArchiveGroup.targetEpisode as any).imageUrl).trim()}")`,
                    }}
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(4,7,13,0.88),rgba(7,11,19,0.58)_42%,rgba(4,7,13,0.86))]" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(224,204,150,0.14),transparent_28%),linear-gradient(180deg,rgba(4,8,14,0.12),rgba(4,8,14,0.42))]" />
                </>
              ) : null}
              <div className="relative px-4 py-5 sm:px-5 sm:py-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="max-w-[12ch] text-[1.85rem] font-semibold leading-[1.02] text-[#f4efe3] sm:text-[2.2rem]">
                      {formatArchiveHeadingTitle(currentArchiveGroup.archiveTitle)}
                    </h3>
                    <p className="mt-3 max-w-[28rem] text-sm leading-relaxed text-[#d7d0c1]">
                      {currentArchiveGroup.targetEpisodeTitle}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-[#485364] bg-[rgba(6,11,18,0.44)] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#f0eadc] backdrop-blur-sm sm:px-4 sm:text-xs">
                    Live
                  </span>
                </div>
                {currentArchiveGroup.openPath ? (
                  <Link
                    to={currentArchiveGroup.openPath}
                    className="mt-5 inline-flex items-center rounded-full border border-[#6a6356] bg-[linear-gradient(180deg,rgba(39,41,39,0.48),rgba(12,16,24,0.64))] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f2ebdb] transition hover:border-[#93866b] hover:bg-[linear-gradient(180deg,rgba(56,58,54,0.58),rgba(13,18,26,0.72))] sm:text-xs"
                  >
                    Open Latest Episode
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Archive Browser</p>
            <p className="mt-1 text-sm text-[#b6b1a3]">
              {displayedArchiveGroups.length > 0
                ? "Pick an archive, then open any unlocked episode in that investigation."
                : "Archive investigations appear here only after at least one episode is unlocked."}
            </p>
            {isInactiveMember ? (
              <button
                type="button"
                onClick={() => openMembershipCheckout("inactive_archive_browser")}
                className="mt-4 inline-flex items-center rounded-full border border-amber-200/35 bg-amber-200/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-50 transition hover:bg-amber-200/15 sm:text-xs"
              >
                Reactivate For New Archive Drops
              </button>
            ) : null}
          </div>

          {displayedArchiveGroups.length > 0 ? (
            <>
              <div
                className="mt-4 flex gap-2 overflow-x-auto pb-2"
                role="tablist"
                aria-label="Archive browser"
              >
                {displayedArchiveGroups.map((archiveGroup) => {
                  const isSelected =
                    Number(selectedArchiveGroup?.archiveNumber || 0) === Number(archiveGroup.archiveNumber || 0);
                  return (
                    <button
                      key={`archive-tab-${archiveGroup.archiveNumber}`}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      aria-controls={`archive-panel-${archiveGroup.archiveNumber}`}
                      id={`archive-tab-${archiveGroup.archiveNumber}`}
                      onClick={() => setSelectedArchiveNumber(Number(archiveGroup.archiveNumber || 0))}
                      className={`shrink-0 rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition sm:text-xs ${
                        isSelected
                          ? "border-[#6d6558] bg-[linear-gradient(180deg,rgba(58,55,47,0.82),rgba(19,23,31,0.9))] text-[#f4ecda] shadow-[0_10px_24px_rgba(0,0,0,0.22)]"
                          : "border-[#2d3442] bg-[#111722] text-[#aeb6c3] hover:border-[#465066] hover:text-[#e8e0cd]"
                      }`}
                    >
                      {formatArchiveHeadingTitle(archiveGroup.archiveTitle)}
                    </button>
                  );
                })}
              </div>

              {selectedArchiveGroup ? (
                <div
                  id={`archive-panel-${selectedArchiveGroup.archiveNumber}`}
                  role="tabpanel"
                  aria-labelledby={`archive-tab-${selectedArchiveGroup.archiveNumber}`}
                  className="mt-4 rounded-[1.4rem] border border-[#202735] bg-[linear-gradient(180deg,rgba(13,18,28,0.92),rgba(8,12,19,0.94))] p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-[#f4efe3] sm:text-xl">
                      {formatArchiveHeadingTitle(selectedArchiveGroup.archiveTitle)}
                    </h3>
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">
                        Page {archiveEpisodePage + 1} of {selectedArchiveEpisodePageCount}
                      </p>
                      <button
                        type="button"
                        onClick={() => setArchiveEpisodePage((current) => Math.max(0, current - 1))}
                        disabled={archiveEpisodePage <= 0}
                        className="rounded-full border border-[#353b49] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d5cec0] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setArchiveEpisodePage((current) =>
                            Math.min(selectedArchiveEpisodePageCount - 1, current + 1)
                          )
                        }
                        disabled={archiveEpisodePage >= selectedArchiveEpisodePageCount - 1}
                        className="rounded-full border border-[#353b49] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d5cec0] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  <ul className="mt-4 space-y-3">
                    {paginatedArchiveEpisodes.map((episode: any) => {
                      const episodePath = buildAgencyArchiveEpisodePath(
                        Number(episode.archiveNumber || 0),
                        Number(episode.episodeNumber || 0),
                        String(episode.title || episode.label || "")
                      );
                      const episodeImageUrl = String(episode.imageUrl || "").trim();

                      return (
                        <li
                          key={`archive-episode-${selectedArchiveGroup.archiveNumber}-${episode.episodeNumber}`}
                          className="relative overflow-hidden rounded-[1.15rem] border border-[#293141] bg-[#111622]/75 min-h-[7.75rem]"
                        >
                          {episodeImageUrl ? (
                            <>
                              <div
                                className="absolute inset-0 bg-cover bg-center opacity-55 saturate-[0.78]"
                                style={{ backgroundImage: `url("${episodeImageUrl}")` }}
                              />
                              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,10,18,0.94),rgba(7,12,21,0.8)_42%,rgba(5,10,18,0.88))]" />
                            </>
                          ) : null}
                          <div className="relative flex h-full flex-col justify-between gap-3 p-3.5 sm:p-4">
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-[0.2em] text-[#c6baa0]">
                                {formatArchiveEpisodeTag(episode.episodeNumber)}
                              </p>
                              <p className="mt-2 max-w-[32rem] text-[1.15rem] font-semibold leading-snug text-[#f3ede1] sm:text-[1.3rem]">
                                {String(
                                  formatArchiveEpisodeTitle(episode.title) ||
                                    `${formatArchiveHeadingTitle(selectedArchiveGroup.archiveTitle)}`
                                )}
                              </p>
                              <Link
                                to={episodePath}
                                className="mt-3 inline-flex w-fit items-center rounded-full border border-[#353b49] bg-[rgba(9,15,24,0.42)] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#d5cec0] transition hover:bg-white/5"
                              >
                                Open Episode
                              </Link>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-4 rounded-[1.4rem] border border-[#202735] bg-[linear-gradient(180deg,rgba(13,18,28,0.92),rgba(8,12,19,0.94))] p-4 text-sm leading-relaxed text-[#b6b1a3]">
              No archive investigations are readable yet. This browser will populate once your first
              episode is unlocked.
            </div>
          )}
        </article>
      </section>

      <p className="mt-10 text-center text-xs uppercase tracking-[0.28em] text-[#7f7a70]">
        Cold Case Agency / Internal Member Record
      </p>
    </section>
  );
};

export default ClubDashboardPage;
